import React, { useState, useEffect } from 'react';

const AIFoundationLayout = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredSection, setHoveredSection] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const containerStyle = {
    backgroundColor: 'white',
    minHeight: '100vh',
    padding: '60px 20px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    position: 'relative',
    overflow: 'hidden'
  };

  // Floating background elements
  const backgroundElements = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 1
  };

  const floatingElement1 = {
    position: 'absolute',
    top: '10%',
    right: '10%',
    width: '100px',
    height: '100px',
    background: 'linear-gradient(45deg, #4A90E2, #6BA3F5)',
    borderRadius: '50%',
    opacity: '0.1',
    animation: 'float 6s ease-in-out infinite'
  };

  const floatingElement2 = {
    position: 'absolute',
    bottom: '20%',
    left: '5%',
    width: '80px',
    height: '80px',
    background: 'linear-gradient(45deg, #90C3F8, #4A90E2)',
    borderRadius: '30%',
    opacity: '0.08',
    animation: 'float 8s ease-in-out infinite reverse'
  };

  const contentWrapperStyle = {
    position: 'relative',
    zIndex: 2
  };

  const statsContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    marginBottom: '80px',
    flexWrap: 'wrap',
    transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const getStatCardStyle = (index) => ({
    backgroundColor: hoveredCard === index ? '#ffffff' : '#f8fafc',
    padding: '40px 50px',
    borderRadius: '20px',
    textAlign: 'center',
    minWidth: '220px',
    boxShadow: hoveredCard === index 
      ? '0 20px 40px rgba(74, 144, 226, 0.2), 0 0 0 1px rgba(74, 144, 226, 0.1)' 
      : '0 10px 30px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    transform: hoveredCard === index ? 'translateY(-10px) scale(1.05)' : 'translateY(0) scale(1)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    background: hoveredCard === index 
      ? 'linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)' 
      : '#f8fafc',
    border: hoveredCard === index ? '2px solid rgba(74, 144, 226, 0.2)' : '2px solid transparent'
  });

  const statNumberStyle = {
    fontSize: '52px',
    fontWeight: '800',
    background: 'linear-gradient(135deg, #4A90E2 0%, #6BA3F5 50%, #90C3F8 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    margin: '0 0 12px 0',
    letterSpacing: '-1px'
  };

  const statLabelStyle = {
    fontSize: '16px',
    color: '#64748b',
    margin: '0',
    fontWeight: '500',
    letterSpacing: '0.5px'
  };

  const mainContentStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '50px',
    maxWidth: '1300px',
    margin: '0 auto',
    flexWrap: 'wrap',
    transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
    opacity: isVisible ? 1 : 0,
    transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1) 0.3s'
  };

  const getSectionStyle = (index) => ({
    flex: '1',
    minWidth: '350px',
    maxWidth: '550px',
    transform: hoveredSection === index ? 'translateY(-5px)' : 'translateY(0)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  });

  const imageContainerStyle = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '24px',
    marginBottom: '30px',
    boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
  };

  const imageStyle = {
    width: '100%',
    height: '220px',
    borderRadius: '24px',
    objectFit: 'cover',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: hoveredSection !== null ? 'scale(1.05)' : 'scale(1)'
  };

  const imageOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(74, 144, 226, 0.1), rgba(144, 195, 248, 0.1))',
    opacity: hoveredSection !== null ? 1 : 0,
    transition: 'opacity 0.3s ease'
  };

  const sectionHeaderStyle = {
    fontSize: '12px',
    fontWeight: '700',
    color: '#94a3b8',
    letterSpacing: '2px',
    marginBottom: '16px',
    textTransform: 'uppercase'
  };

  const titleStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
    lineHeight: '1.3',
    letterSpacing: '-0.5px'
  };

  const descriptionStyle = {
    fontSize: '17px',
    color: '#64748b',
    lineHeight: '1.7',
    marginBottom: '28px',
    fontWeight: '400'
  };

  const linkStyle = {
    color: '#4A90E2',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    position: 'relative',
    display: 'inline-block',
    transition: 'all 0.3s ease',
    paddingBottom: '2px'
  };

  const linkUnderlineStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, #4A90E2, #6BA3F5)',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  // Keyframes for animations
  const keyframes = `
    @keyframes float {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-20px) rotate(5deg); }
    }
    
    @keyframes pulse {
      0%, 100% { opacity: 0.8; }
      50% { opacity: 1; }
    }

    @keyframes slideInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div style={containerStyle}>
        {/* Floating background elements */}
        <div style={backgroundElements}>
          <div style={floatingElement1}></div>
          <div style={floatingElement2}></div>
        </div>

        <div style={contentWrapperStyle}>
          {/* Stats Section */}

          {/* Main Content Section */}
          <div style={mainContentStyle}>
            {/* Foundation Section */}
            <div 
              style={getSectionStyle(0)}
              onMouseEnter={() => setHoveredSection(0)}
              onMouseLeave={() => setHoveredSection(null)}
            >
              <div style={imageContainerStyle}>
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyMCIgdmlld0JveD0iMCAwIDQwMCAyMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9ImJnIiB4MT0iMCUiIHkxPSIwJSIgeDI9IjEwMCUiIHkyPSIxMDAlIj4KPHN0b3Agb2Zmc2V0PSIwJSIgc3R5bGU9InN0b3AtY29sb3I6I0YwRjhGRjtzdG9wLW9wYWNpdHk6MSIgLz4KPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdHlsZT0ic3RvcC1jb2xvcjojRTBGMkZFO3N0b3Atb3BhY2l0eToxIiAvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMjIwIiBmaWxsPSJ1cmwoI2JnKSIvPgo8Y2lyY2xlIGN4PSI4MCIgY3k9IjgwIiByPSIyNSIgZmlsbD0iIzRBOTBFMiIgb3BhY2l0eT0iMC44Ii8+CjxjaXJjbGUgY3g9IjE2MCIgY3k9IjYwIiByPSIyMCIgZmlsbD0iIzZCQTNGNSIgb3BhY2l0eT0iMC43Ii8+CjxjaXJjbGUgY3g9IjI0MCIgY3k9IjkwIiByPSIzMCIgZmlsbD0iIzkwQzNGOCIgb3BhY2l0eT0iMC42Ii8+CjxjaXJjbGUgY3g9IjMyMCIgY3k9IjcwIiByPSIyMiIgZmlsbD0iIzRBOTBFMiIgb3BhY2l0eT0iMC41Ii8+CjxyZWN0IHg9IjUwIiB5PSIxNDAiIHdpZHRoPSI2MCIgaGVpZ2h0PSI0MCIgcng9IjgiIGZpbGw9IiM0QTkwRTIiIG9wYWNpdHk9IjAuMyIvPgo8cmVjdCB4PSIxNDAiIHk9IjE1MCIgd2lkdGg9IjcwIiBoZWlnaHQ9IjMwIiByeD0iNiIgZmlsbD0iIzZCQTNGNSIgb3BhY2l0eT0iMC40Ii8+CjxyZWN0IHg9IjI0MCIgeT0iMTYwIiB3aWR0aD0iNTAiIGhlaWdodD0iMjAiIHJ4PSI0IiBmaWxsPSIjOTBDM0Y4IiBvcGFjaXR5PSIwLjMiLz4KPC9zdmc+"
                  alt="Team collaboration workspace"
                  style={imageStyle}
                />
                <div style={imageOverlayStyle}></div>
              </div>
              <div style={sectionHeaderStyle}>WE ARE FOUNDATION</div>
              <h3 style={titleStyle}>Leading the way in ethical AI collaboration</h3>
              <p style={descriptionStyle}>
                Global AI Society Foundation is taking the AI revolution to the next level 
                by establishing a life-changing collaboration between humans and machines 
                through accessible education and cutting-edge research.
              </p>
              <div style={linkUnderlineStyle}></div>
            </div>

            {/* Research Section */}
            <div 
              style={getSectionStyle(1)}
              onMouseEnter={() => setHoveredSection(1)}
              onMouseLeave={() => setHoveredSection(null)}
            >
              <div style={imageContainerStyle}>
                <img 
                  src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyMCIgdmlld0JveD0iMCAwIDQwMCAyMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxkZWZzPgo8bGluZWFyR3JhZGllbnQgaWQ9ImRhcmtCZyIgeDE9IjAlIiB5MT0iMCUiIHgyPSIxMDAlIiB5Mj0iMTAwJSI+CjxzdG9wIG9mZnNldD0iMCUiIHN0eWxlPSJzdG9wLWNvbG9yOiMxQTIzMzM7c3RvcC1vcGFjaXR5OjEiIC8+CjxzdG9wIG9mZnNldD0iMTAwJSIgc3R5bGU9InN0b3AtY29sb3I6IzBGMTYyMTtzdG9wLW9wYWNpdHk6MSIgLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyMCIgZmlsbD0idXJsKCNkYXJrQmcpIi8+CjxyZWN0IHg9IjIwIiB5PSIzMCIgd2lkdGg9IjMiIGhlaWdodD0iMTYwIiBmaWxsPSIjMDBGRjAwIiBvcGFjaXR5PSIwLjgiLz4KPHJlY3QgeD0iMzUiIHk9IjUwIiB3aWR0aD0iMyIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNGRjY2MDAiIG9wYWNpdHk9IjAuNyIvPgo8cmVjdCB4PSI1MCIgeT0iNDAiIHdpZHRoPSIzIiBoZWlnaHQ9IjE0MCIgZmlsbD0iI0ZGMDA2NiIgb3BhY2l0eT0iMC44Ii8+CjxyZWN0IHg9IjY1IiB5PSI3MCIgd2lkdGg9IjMiIGhlaWdodD0iMTAwIiBmaWxsPSIjNjZGRkZGIiBvcGFjaXR5PSIwLjYiLz4KPHJlY3QgeD0iODAiIHk9IjYwIiB3aWR0aD0iMyIgaGVpZ2h0PSIxMTAiIGZpbGw9IiNGRkZGMDAiIG9wYWNpdHk9IjAuNyIvPgo8cmVjdCB4PSI5NSIgeT0iODAiIHdpZHRoPSIzIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRkY2NkZGIiBvcGFjaXR5PSIwLjUiLz4KPHJlY3QgeD0iMTEwIiB5PSI1NSIgd2lkdGg9IjMiIGhlaWdodD0iMTI1IiBmaWxsPSIjNjZGRjAwIiBvcGFjaXR5PSIwLjgiLz4KPHJlY3QgeD0iMTI1IiB5PSI2NSIgd2lkdGg9IjMiIGhlaWdodD0iMTA1IiBmaWxsPSIjMDA2NkZGIiBvcGFjaXR5PSIwLjYiLz4KPHJlY3QgeD0iMTQwIiB5PSI0NSIgd2lkdGg9IjMiIGhlaWdodD0iMTM1IiBmaWxsPSIjRkYwMEZGIiBvcGFjaXR5PSIwLjciLz4KPHJlY3QgeD0iMTU1IiB5PSI3NSIgd2lkdGg9IjMiIGhlaWdodD0iOTUiIGZpbGw9IiMwMEZGRkYiIG9wYWNpdHk9IjAuNSIvPgo8L3N2Zz4="
                  alt="Code on screen"
                  style={imageStyle}
                />
                <div style={imageOverlayStyle}></div>
              </div>
              <div style={sectionHeaderStyle}>WE DO RESEARCH</div>
              <h3 style={titleStyle}>Advancing AI education worldwide</h3>
              <p style={descriptionStyle}>
                Our scientific and research work is focused on creating empathetic 
                artificial intelligence education programs that support the well-being of 
                learners and meaningfully advance humanity's understanding of AI.
              </p>
              <div style={linkUnderlineStyle}></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIFoundationLayout;